import type { Conversation, ConversationSummary } from "@/types";
import type { ConversationRepository } from "./ConversationRepository";
import { toConversationSummary } from "./summarize";

/**
 * localStorage-backed conversation repository.
 *
 * Used only when the app is running outside the Tauri shell (`pnpm dev` in a
 * plain browser), so the chat feature stays testable without launching the
 * native app. Mirrors the same key layout style as the Tauri implementation:
 * one entry per conversation plus a summary index.
 */
const INDEX_KEY = "seufic.conversations.index";
const conversationKey = (id: string) => `seufic.conversations.item.${id}`;

function readIndex(): ConversationSummary[] {
  try {
    const raw = localStorage.getItem(INDEX_KEY);
    return raw ? (JSON.parse(raw) as ConversationSummary[]) : [];
  } catch {
    return [];
  }
}

function writeIndex(index: ConversationSummary[]): void {
  localStorage.setItem(INDEX_KEY, JSON.stringify(index));
}

// Not `async` — these are synchronous under the hood, but the interface is
// `Promise`-returning (to match the Tauri store implementation), so each
// method wraps its result explicitly instead of paying for an `async` that
// never awaits anything.
export const localConversationRepository: ConversationRepository = {
  list() {
    return Promise.resolve([...readIndex()].sort((a, b) => b.updatedAt - a.updatedAt));
  },

  get(id) {
    try {
      const raw = localStorage.getItem(conversationKey(id));
      return Promise.resolve(raw ? (JSON.parse(raw) as Conversation) : null);
    } catch {
      return Promise.resolve(null);
    }
  },

  save(conversation) {
    localStorage.setItem(conversationKey(conversation.id), JSON.stringify(conversation));
    const index = readIndex().filter((s) => s.id !== conversation.id);
    index.push(toConversationSummary(conversation));
    writeIndex(index);
    return Promise.resolve();
  },

  delete(id) {
    localStorage.removeItem(conversationKey(id));
    writeIndex(readIndex().filter((s) => s.id !== id));
    return Promise.resolve();
  },
};
